import React, { useState, useEffect, useMemo, useCallback } from "react"
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table"
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  ChevronLeft,
  Download,
  RefreshCw,
  TrendingDown,
  TrendingUp,
  Equal,
  BarChart3,
  FileText,
} from "lucide-react"
import * as XLSX from "xlsx"

// ** UI Components
import { Card, CardContent, CardHeader, CardTitle } from "@components/common/card"
import { Badge } from "@components/common/badge"
import { Button } from "@components/common/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/common/table"
import PmsLoader from "@components/common/loader"

// ** Services / Utils
import axiosServices from "@pma/utils/axios"
import {
  ALL_MAIN_BOQ_ENDPOINT,
  FINANCIAL_SERVER_URL,
  GET_ESTIMATE_BOQ_ENDPOINT,
  GET_MAIN_BOQ_ENDPOINT,
  BILLS_BY_BOQ_ID__LIST_ENDPOINT,
} from "@pma/utils/api-routes"
import { cn } from "@pma/utils/helpers"
import { formatCurrency } from "@pma/utils/utils"

// ** Hooks
import useTranslator from "@pma/hooks/use-translator"
import { useAuth } from "@pma/stores/auth-store"

// ** Xano semantic layer
import { normalizeBoq, normalizeBoqItem, extractArray } from "@pma/types/xano-models"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface EstimateVsActualProps {
  estimateBoqId: string
  projectId?: string
}

interface EstimateItem {
  id: string
  number: string
  description: string
  units: string
  quantity: number
  price: number
  amount: number
  sum: string | number
  is_item: boolean
}

interface MatchedRow {
  id: string
  number: string
  description: string
  units: string
  estimateQty: number
  estimateAmount: number
  contractQty: number
  contractAmount: number
  billedAmount: number
  deltaPercent: number
  isItem: boolean
  billHistory: BillHistoryEntry[]
}

interface BillHistoryEntry {
  billNumber: string | number
  date: string
  requestedQty: number
  approvedQty: number
  requestedSum: number
  approvedSum: number
  paidSum: number
  status: string
}

type SortDirection = "asc" | "desc" | null

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getDeltaColor(delta: number): string {
  if (delta < 0) return "text-blue-600 bg-blue-50"
  if (delta <= 5) return "text-green-600 bg-green-50"
  if (delta <= 20) return "text-yellow-600 bg-yellow-50"
  return "text-red-600 bg-red-50"
}

function getDeltaBadgeVariant(delta: number, isUnder: boolean): "default" | "secondary" | "destructive" | "outline" {
  if (isUnder) return "secondary"
  if (delta <= 5) return "secondary"
  if (delta <= 20) return "default"
  return "destructive"
}

function getDeltaIcon(delta: number, isUnder: boolean) {
  if (isUnder) return <TrendingDown className="h-3 w-3" />
  if (delta <= 5) return <Equal className="h-3 w-3" />
  return <TrendingUp className="h-3 w-3" />
}

function getDeltaLabel(delta: number, isUnder: boolean, translate: (key: string) => string): string {
  if (isUnder) return translate("under_estimate")
  if (delta <= 5) return translate("within_estimate")
  return translate("over_estimate")
}

function safeNum(v: any): number {
  const n = Number(v)
  return isNaN(n) ? 0 : n
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function EstimateVsActual({ estimateBoqId, projectId }: EstimateVsActualProps) {
  const { translate, locale } = useTranslator()
  const { authConfig } = useAuth()
  const isHebrew = locale === "he"
  const orgId = authConfig?.last_organization_id

  // -- State ----------------------------------------------------------------
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [estimateItems, setEstimateItems] = useState<EstimateItem[]>([])
  const [matchedRows, setMatchedRows] = useState<MatchedRow[]>([])
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [hasContractBoq, setHasContractBoq] = useState(false)
  const [deltaSortDir, setDeltaSortDir] = useState<SortDirection>(null)

  // -- Data fetching --------------------------------------------------------
  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      // 1. Fetch estimate BOQ items
      const estimateRes = await axiosServices.post(
        FINANCIAL_SERVER_URL + GET_ESTIMATE_BOQ_ENDPOINT,
        { boq_estimate_id: estimateBoqId },
      )
      const estItems: EstimateItem[] = estimateRes.data?.boq_items || []
      setEstimateItems(estItems)

      // 2. Fetch all Main BOQs to find ones referencing this estimate
      const mainBoqsRes = await axiosServices.post(
        FINANCIAL_SERVER_URL + ALL_MAIN_BOQ_ENDPOINT,
        {
          organization_id: orgId,
          ...(projectId ? { project_id: Number(projectId) } : {}),
          show_deleted: false,
        },
      )
      const allMainBoqs = extractArray(mainBoqsRes.data, normalizeBoq)

      const matchingBoqs = allMainBoqs.filter(
        (boq) => String(boq.estimateBoqId) === String(estimateBoqId),
      )

      if (matchingBoqs.length === 0) {
        setHasContractBoq(false)
        setMatchedRows([])
        setLoading(false)
        return
      }

      setHasContractBoq(true)

      // 3. For each matching Main BOQ, get detailed items
      const boqDetailsPromises = matchingBoqs.map((boq) =>
        axiosServices.post(FINANCIAL_SERVER_URL + GET_MAIN_BOQ_ENDPOINT, {
          boq_id: boq.id,
        }),
      )
      const boqDetailsResults = await Promise.allSettled(boqDetailsPromises)

      // 4. Also fetch bills for each matching BOQ
      const billPromises = matchingBoqs.map((boq) =>
        axiosServices.post(FINANCIAL_SERVER_URL + BILLS_BY_BOQ_ID__LIST_ENDPOINT, {
          organization_id: orgId,
          boq_id: boq.id,
          type: "all",
        }),
      )
      const billResults = await Promise.allSettled(billPromises)

      // 5. Build a map: estimate_item_id -> aggregated contract data
      const contractMap = new Map<
        string,
        {
          contractQty: number
          contractAmount: number
          billedAmount: number
          paid: number
          billHistory: BillHistoryEntry[]
        }
      >()

      boqDetailsResults.forEach((result, boqIndex) => {
        if (result.status !== "fulfilled") return
        const rawBoqItems: any[] = result.value.data?.boq_items || []
        const normalizedItems = rawBoqItems.map(normalizeBoqItem)
        const boqBills =
          billResults[boqIndex]?.status === "fulfilled"
            ? (billResults[boqIndex] as PromiseFulfilledResult<any>).value.data
            : []
        const billsArr = Array.isArray(boqBills) ? boqBills : boqBills?.items || []

        normalizedItems.forEach((item, itemIndex) => {
          if (!item.isItem) return
          const rawItem = rawBoqItems[itemIndex]
          const estItemId = String(rawItem?.estimate_item_id || item.id)

          const existing = contractMap.get(estItemId) || {
            contractQty: 0,
            contractAmount: 0,
            billedAmount: 0,
            paid: 0,
            billHistory: [],
          }

          existing.contractQty += item.quantity
          existing.contractAmount += item.realTotal || item.contractTotal
          existing.billedAmount += item.paidTotal
          existing.paid += item.paidQuantity

          // Build bill history from bills
          billsArr.forEach((bill: any) => {
            const billItems = bill?._bill_items || []
            const billItem = billItems.find(
              (bi: any) => String(bi?._boq_item?.id) === String(item.id),
            )
            if (billItem && (billItem.amount || billItem.approved_amount || billItem.paid_sum)) {
              existing.billHistory.push({
                billNumber: bill?.number || bill?.id || "?",
                date: bill?.created_at || "",
                requestedQty: safeNum(billItem?.amount),
                approvedQty: safeNum(billItem?.approved_amount || billItem?.amount_by_signer),
                requestedSum: safeNum(billItem?.requested_sum),
                approvedSum: safeNum(billItem?.approved_sum),
                paidSum: safeNum(billItem?.paid_sum),
                status: bill?.is_signed
                  ? "signed"
                  : bill?.is_approved
                    ? "approved"
                    : bill?.is_declined
                      ? "declined"
                      : "pending",
              })
            }
          })

          contractMap.set(estItemId, existing)
        })
      })

      // 6. Match estimate items with contract data
      const rows: MatchedRow[] = estItems
        .filter((ei) => ei.is_item)
        .map((ei) => {
          const contract = contractMap.get(String(ei.id))
          const estAmt = safeNum(ei.sum)
          const contractAmt = contract?.contractAmount || 0
          const billedAmt = contract?.billedAmount || 0

          let deltaPercent = 0
          if (estAmt > 0) {
            deltaPercent = ((contractAmt - estAmt) / estAmt) * 100
          }

          return {
            id: ei.id,
            number: ei.number,
            description: ei.description,
            units: ei.units,
            estimateQty: safeNum(ei.amount),
            estimateAmount: estAmt,
            contractQty: contract?.contractQty || 0,
            contractAmount: contractAmt,
            billedAmount: billedAmt,
            deltaPercent: Math.round(deltaPercent * 10) / 10,
            isItem: ei.is_item,
            billHistory: contract?.billHistory || [],
          }
        })

      setMatchedRows(rows)
      setLoading(false)
    } catch (err: any) {
      setError(err?.message || "Failed to load comparison data")
      setLoading(false)
    }
  }, [estimateBoqId, projectId, orgId])

  useEffect(() => {
    if (estimateBoqId && orgId) {
      fetchData()
    }
  }, [fetchData, estimateBoqId, orgId])

  // -- Sorted rows (Gap 1: sort by delta %) ---------------------------------
  const sortedRows = useMemo(() => {
    if (!deltaSortDir) return matchedRows
    const sorted = [...matchedRows]
    sorted.sort((a, b) =>
      deltaSortDir === "asc"
        ? a.deltaPercent - b.deltaPercent
        : b.deltaPercent - a.deltaPercent,
    )
    return sorted
  }, [matchedRows, deltaSortDir])

  // -- Computed summaries ---------------------------------------------------
  const summary = useMemo(() => {
    const totalEstimate = matchedRows.reduce((s, r) => s + r.estimateAmount, 0)
    const totalContract = matchedRows.reduce((s, r) => s + r.contractAmount, 0)
    const totalBilled = matchedRows.reduce((s, r) => s + r.billedAmount, 0)
    const overallDelta = totalEstimate > 0 ? ((totalContract - totalEstimate) / totalEstimate) * 100 : 0

    return {
      totalEstimate,
      totalContract,
      totalBilled,
      overallDelta: Math.round(overallDelta * 10) / 10,
    }
  }, [matchedRows])

  // -- Sort toggle (Gap 1) --------------------------------------------------
  const toggleDeltaSort = () => {
    setDeltaSortDir((prev) => {
      if (prev === null) return "asc"
      if (prev === "asc") return "desc"
      return null
    })
  }

  // -- Export to Excel (Gap 5) ----------------------------------------------
  const handleExport = useCallback(() => {
    const headers = isHebrew
      ? [
          "% סטייה",
          "סכום מחויב",
          "סכום חוזי",
          "כמות חוזית",
          "סכום אומדן",
          "כמות אומדן",
          "יחידה",
          "תיאור",
          "סעיף",
        ]
      : [
          "Item #",
          "Description",
          "Unit",
          "Estimate Qty",
          "Estimate Amount",
          "Contract Qty",
          "Contract Amount",
          "Billed Amount",
          "Delta %",
        ]

    const excelData = sortedRows.map((row) => {
      const enValues = [
        row.number,
        row.description,
        row.units,
        row.estimateQty,
        row.estimateAmount,
        row.contractQty,
        row.contractAmount,
        row.billedAmount,
        row.deltaPercent,
      ]
      const heValues = [
        row.deltaPercent,
        row.billedAmount,
        row.contractAmount,
        row.contractQty,
        row.estimateAmount,
        row.estimateQty,
        row.units,
        row.description,
        row.number,
      ]

      return headers.reduce((acc: Record<string, any>, key, idx) => {
        acc[key] = isHebrew ? heValues[idx] : enValues[idx]
        return acc
      }, {})
    })

    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.json_to_sheet(excelData)

    const colWidths = headers.map((h) => ({ wch: Math.max(h.length + 2, 14) }))
    ws["!cols"] = colWidths

    XLSX.utils.book_append_sheet(
      wb,
      ws,
      isHebrew ? "אומדן מול ביצוע" : "Estimate vs Actual",
    )

    const filename = `estimate-vs-actual-${new Date().toISOString().slice(0, 10)}.xlsx`

    try {
      XLSX.writeFile(wb, filename, { bookType: "xlsx", type: "binary" })
    } catch {
      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" })
      const blob = new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    }
  }, [sortedRows, isHebrew])

  // -- Expand/collapse ------------------------------------------------------
  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  // -- Table columns --------------------------------------------------------
  const columns = useMemo(
    (): ColumnDef<MatchedRow>[] => [
      {
        accessorKey: "number",
        header: () => <span className="text-xs font-semibold">#</span>,
        cell: ({ row }) => {
          const hasHistory = row.original.billHistory.length > 0
          const isExpanded = expandedRows.has(row.original.id)
          return (
            <div className="flex items-center gap-1">
              {hasHistory && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleRow(row.original.id)
                  }}
                  className="flex h-5 w-5 items-center justify-center rounded hover:bg-muted"
                >
                  {isExpanded ? (
                    <ChevronDown className="h-3 w-3" />
                  ) : isHebrew ? (
                    <ChevronLeft className="h-3 w-3" />
                  ) : (
                    <ChevronLeft className="h-3 w-3 rotate-180" />
                  )}
                </button>
              )}
              <span className="font-mono text-xs">{row.original.number}</span>
            </div>
          )
        },
        size: 80,
        enableSorting: false,
      },
      {
        accessorKey: "description",
        header: () => (
          <span className="text-xs font-semibold">{translate("Description")}</span>
        ),
        cell: ({ row }) => (
          <div className="line-clamp-2 text-xs leading-tight">
            {row.original.description}
          </div>
        ),
        size: 200,
        enableSorting: false,
      },
      {
        accessorKey: "units",
        header: () => (
          <span className="text-xs font-semibold">{translate("Unit")}</span>
        ),
        cell: ({ row }) => (
          <div className="text-center text-xs">{row.original.units}</div>
        ),
        size: 50,
        enableSorting: false,
      },
      {
        id: "estimateQty",
        header: () => (
          <span className="text-xs font-semibold">{translate("estimate_quantity")}</span>
        ),
        cell: ({ row }) => (
          <div className="text-center font-mono text-xs">{row.original.estimateQty || ""}</div>
        ),
        size: 70,
        enableSorting: false,
      },
      {
        id: "estimateAmount",
        header: () => (
          <span className="text-xs font-semibold">{translate("estimate_amount")}</span>
        ),
        cell: ({ row }) => (
          <div className="text-center font-mono text-xs">
            {formatCurrency(row.original.estimateAmount, isHebrew)}
          </div>
        ),
        size: 100,
        enableSorting: false,
      },
      {
        id: "contractQty",
        header: () => (
          <span className="text-xs font-semibold">{translate("contract_quantity")}</span>
        ),
        cell: ({ row }) => (
          <div className="text-center font-mono text-xs">{row.original.contractQty || ""}</div>
        ),
        size: 70,
        enableSorting: false,
      },
      {
        id: "contractAmount",
        header: () => (
          <span className="text-xs font-semibold">{translate("contract_amount")}</span>
        ),
        cell: ({ row }) => (
          <div className="text-center font-mono text-xs">
            {formatCurrency(row.original.contractAmount, isHebrew)}
          </div>
        ),
        size: 100,
        enableSorting: false,
      },
      {
        id: "billedAmount",
        header: () => (
          <span className="text-xs font-semibold">{translate("billed_amount")}</span>
        ),
        cell: ({ row }) => (
          <div className="text-center font-mono text-xs">
            {formatCurrency(row.original.billedAmount, isHebrew)}
          </div>
        ),
        size: 100,
        enableSorting: false,
      },
      {
        id: "deltaPercent",
        header: () => (
          <button
            onClick={toggleDeltaSort}
            className="flex w-full items-center justify-center gap-1 text-xs font-semibold hover:text-foreground"
          >
            {translate("delta_percent")}
            {deltaSortDir === "asc" ? (
              <ArrowUp className="h-3 w-3" />
            ) : deltaSortDir === "desc" ? (
              <ArrowDown className="h-3 w-3" />
            ) : (
              <ArrowUpDown className="h-3 w-3 opacity-50" />
            )}
          </button>
        ),
        cell: ({ row }) => {
          const delta = row.original.deltaPercent
          const isUnder = delta < 0
          const absDelta = Math.abs(delta)
          return (
            <div className="flex items-center justify-center">
              <Badge
                variant={getDeltaBadgeVariant(absDelta, isUnder)}
                className={cn("gap-1 text-[10px]", getDeltaColor(delta))}
              >
                {getDeltaIcon(absDelta, isUnder)}
                {isUnder ? "-" : "+"}
                {absDelta}%
              </Badge>
            </div>
          )
        },
        size: 90,
        enableSorting: false,
      },
    ],
    [expandedRows, isHebrew, translate, deltaSortDir],
  )

  const table = useReactTable({
    data: sortedRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  })

  // -- Render ---------------------------------------------------------------

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <PmsLoader />
      </div>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-8 text-center text-sm text-destructive">
          {error}
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            className="gap-1"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            {translate("Retry")}
          </Button>
        </CardContent>
      </Card>
    )
  }

  if (!hasContractBoq) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          {translate("no_contract_boq")}
        </CardContent>
      </Card>
    )
  }

  const overallIsUnder = summary.overallDelta < 0
  const overallAbsDelta = Math.abs(summary.overallDelta)

  return (
    <div className={cn("space-y-4", isHebrew ? "rtl" : "ltr")} dir={isHebrew ? "rtl" : "ltr"}>
      {/* KPI Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {/* Total Estimate */}
        <Card>
          <CardHeader className="px-3 py-3">
            <CardTitle className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
              <FileText className="h-4 w-4" />
              {translate("estimate_amount")}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3 pt-0">
            <div className="text-lg font-bold">
              {formatCurrency(summary.totalEstimate, isHebrew)}
            </div>
          </CardContent>
        </Card>

        {/* Total Contract */}
        <Card>
          <CardHeader className="px-3 py-3">
            <CardTitle className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
              <BarChart3 className="h-4 w-4" />
              {translate("contract_amount")}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3 pt-0">
            <div className="text-lg font-bold">
              {formatCurrency(summary.totalContract, isHebrew)}
            </div>
          </CardContent>
        </Card>

        {/* Total Billed */}
        <Card>
          <CardHeader className="px-3 py-3">
            <CardTitle className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
              <TrendingUp className="h-4 w-4" />
              {translate("billed_amount")}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3 pt-0">
            <div className="text-lg font-bold">
              {formatCurrency(summary.totalBilled, isHebrew)}
            </div>
          </CardContent>
        </Card>

        {/* Overall Delta */}
        <Card>
          <CardHeader className="px-3 py-3">
            <CardTitle className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
              {overallIsUnder ? (
                <TrendingDown className="h-4 w-4" />
              ) : (
                <TrendingUp className="h-4 w-4" />
              )}
              {translate("overall_delta")}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3 pt-0">
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold">
                {overallIsUnder ? "-" : "+"}
                {overallAbsDelta}%
              </span>
              <Badge
                variant={getDeltaBadgeVariant(overallAbsDelta, overallIsUnder)}
                className={cn("text-[10px]", getDeltaColor(summary.overallDelta))}
              >
                {getDeltaLabel(overallAbsDelta, overallIsUnder, translate)}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Comparison Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between px-3 py-3 sm:px-4">
          <CardTitle className="text-sm font-semibold sm:text-base">
            {translate("estimate_vs_actual")}
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            className="gap-1"
          >
            <Download className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{translate("Download")}</span>
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="text-xs">
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id} className="border-b bg-gray-50">
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        className={cn(
                          "whitespace-nowrap border-gray-200 px-2 py-2 text-center",
                          isHebrew ? "border-r first:border-r-0" : "border-l first:border-l-0",
                        )}
                        style={{ width: header.getSize() }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.map((row, index) => {
                  const isExpanded = expandedRows.has(row.original.id)
                  const hasBills = row.original.billHistory.length > 0

                  return (
                    <React.Fragment key={row.id}>
                      <TableRow
                        className={cn(
                          "border-b transition-colors hover:bg-gray-50",
                          index % 2 === 0 ? "bg-white" : "bg-gray-25",
                          hasBills && "cursor-pointer",
                        )}
                        onClick={() => hasBills && toggleRow(row.original.id)}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell
                            key={cell.id}
                            className={cn(
                              "border-gray-200 px-2 py-1.5",
                              isHebrew
                                ? "border-r first:border-r-0"
                                : "border-l first:border-l-0",
                            )}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>

                      {/* Expandable bill history */}
                      {isExpanded && hasBills && (
                        <TableRow key={`${row.id}-history`} className="bg-muted/30">
                          <TableCell colSpan={columns.length} className="p-0">
                            <div className="px-4 py-3">
                              <div className="mb-2 text-xs font-semibold text-muted-foreground">
                                {translate("bill_history")}
                              </div>
                              <div className="overflow-x-auto rounded border bg-white">
                                <Table className="text-[11px]">
                                  <TableHeader>
                                    <TableRow className="bg-gray-50">
                                      <TableHead className="px-2 py-1.5 text-center text-[11px]">
                                        {translate("Bill")} #
                                      </TableHead>
                                      <TableHead className="px-2 py-1.5 text-center text-[11px]">
                                        {translate("Date")}
                                      </TableHead>
                                      <TableHead className="px-2 py-1.5 text-center text-[11px]">
                                        {translate("Requested")} ({translate("Amount")})
                                      </TableHead>
                                      <TableHead className="px-2 py-1.5 text-center text-[11px]">
                                        {translate("Bill Approved")} ({translate("Amount")})
                                      </TableHead>
                                      <TableHead className="px-2 py-1.5 text-center text-[11px]">
                                        {translate("billed_amount")}
                                      </TableHead>
                                      <TableHead className="px-2 py-1.5 text-center text-[11px]">
                                        {translate("Status")}
                                      </TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {row.original.billHistory.map((bill, bi) => (
                                      <TableRow key={bi} className="border-b last:border-b-0">
                                        <TableCell className="px-2 py-1 text-center font-mono">
                                          {bill.billNumber}
                                        </TableCell>
                                        <TableCell className="px-2 py-1 text-center">
                                          {bill.date
                                            ? new Date(bill.date).toLocaleDateString(
                                                isHebrew ? "he-IL" : "en-US",
                                              )
                                            : "-"}
                                        </TableCell>
                                        <TableCell className="px-2 py-1 text-center font-mono">
                                          {bill.requestedQty}
                                        </TableCell>
                                        <TableCell className="px-2 py-1 text-center font-mono">
                                          {bill.approvedQty}
                                        </TableCell>
                                        <TableCell className="px-2 py-1 text-center font-mono">
                                          {formatCurrency(bill.paidSum, isHebrew)}
                                        </TableCell>
                                        <TableCell className="px-2 py-1 text-center">
                                          <Badge
                                            variant={
                                              bill.status === "signed"
                                                ? "secondary"
                                                : bill.status === "approved"
                                                  ? "default"
                                                  : bill.status === "declined"
                                                    ? "destructive"
                                                    : "outline"
                                            }
                                            className="text-[10px]"
                                          >
                                            {translate(
                                              bill.status === "signed"
                                                ? "Signed"
                                                : bill.status === "approved"
                                                  ? "Approved"
                                                  : bill.status === "declined"
                                                    ? "Declined"
                                                    : "Pending",
                                            )}
                                          </Badge>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  )
                })}

                {/* Summary row */}
                {matchedRows.length > 0 && (
                  <TableRow className="border-t-2 border-gray-300 bg-gray-100 font-bold">
                    <TableCell className="px-2 py-2" />
                    <TableCell className="px-2 py-2 text-xs font-bold">
                      {translate("Total")}
                    </TableCell>
                    <TableCell className="px-2 py-2" />
                    <TableCell className="px-2 py-2" />
                    <TableCell className="px-2 py-2 text-center font-mono text-xs font-bold">
                      {formatCurrency(summary.totalEstimate, isHebrew)}
                    </TableCell>
                    <TableCell className="px-2 py-2" />
                    <TableCell className="px-2 py-2 text-center font-mono text-xs font-bold">
                      {formatCurrency(summary.totalContract, isHebrew)}
                    </TableCell>
                    <TableCell className="px-2 py-2 text-center font-mono text-xs font-bold">
                      {formatCurrency(summary.totalBilled, isHebrew)}
                    </TableCell>
                    <TableCell className="px-2 py-2 text-center">
                      <Badge
                        variant={getDeltaBadgeVariant(overallAbsDelta, overallIsUnder)}
                        className={cn(
                          "gap-1 text-[10px]",
                          getDeltaColor(summary.overallDelta),
                        )}
                      >
                        {getDeltaIcon(overallAbsDelta, overallIsUnder)}
                        {overallIsUnder ? "-" : "+"}
                        {overallAbsDelta}%
                      </Badge>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
